import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { ProductsFacade, RelatedCardFacade, TravelCardType } from '@ridango/components';
import { AuthService, BaseComponent, PaymentDataService, RelatedCard } from '@ridango/core';
import Env from '@ridango/core/env';
import { EnterNewCardModalComponent, TopUpOtherPersonCardModalComponent } from '@ridango/widgets';
import { combineLatest, Observable } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'gl-wallet-loading-page-v2-select-card',
    templateUrl: './select-card.component.html',
    styleUrls: ['./select-card.component.scss']
})
export class WalletLoadingPageV2SelectCardComponent extends BaseComponent implements OnInit {
    relatedCards$ = this.relatedCardFacade.allRelatedCards$;
    selectedCard$: Observable<RelatedCard | null>;
    selectedCardId$: Observable<number | null>;
    cardForm: FormGroup;
    cardValidationLength: number;
    cardValidationError: boolean;
    travelCardType = TravelCardType;
    @Output() cardFormValidity = new EventEmitter<boolean>();

    @ViewChild('enterNewCardModal') enterNewCardModal: EnterNewCardModalComponent;
    @ViewChild('topUpOtherPersonCardModal') topUpOtherPersonCardModal: TopUpOtherPersonCardModalComponent;
    @ViewChild('matSelect') matSelect!: MatSelect;

    constructor(
        private formBuilder: FormBuilder,
        private relatedCardFacade: RelatedCardFacade,
        @Inject('ENV') public env: Env,
        private productsFacade: ProductsFacade,
        private paymentData: PaymentDataService,
        protected authService: AuthService,
        private activatedRoute: ActivatedRoute
    ) {
        super();
    }

    ngOnInit(): void {
        this.cardValidationLength = this.env.PURCHASE_FLOW?.UTN_TRAVEL_CARD_LENGTH;

        this.cardForm = this.formBuilder.group({
            cardNumber: ['', [Validators.required, Validators.pattern(`^[0-9]{${this.cardValidationLength}}$`)]]
        });

        this.activatedRoute.queryParams.pipe(take(1)).subscribe(({ cardNumber }) => {
            if (cardNumber) {
                this.cardForm.patchValue({ cardNumber });
                this.paymentData.setCard({ id: null, name: cardNumber, card_nr: cardNumber });
            }
        });

        if (this.authService.getLoginStatus()) {
            this.relatedCardFacade.fetchAllRelatedCards();

            this.relatedCards$ = combineLatest([
                this.relatedCardFacade.allRelatedCards$,
                this.paymentData.cardNumber$
            ]).pipe(
                map(([cards, selectedCard]) => {
                    const filtered = cards.filter((card) =>
                        [
                            this.travelCardType.EstId,
                            this.travelCardType.TravelCardU,
                            this.travelCardType.TravelCardUTN2
                        ].includes(card.card_type)
                    );

                    return selectedCard && !filtered.some((card) => card.card_nr === selectedCard.card_nr)
                        ? [selectedCard, ...filtered]
                        : filtered;
                }),
                takeUntil(this.componentDestroyed$)
            );
        }

        this.productsFacade.fetchWalletProducts(this.env.CHANNEL);

        this.selectedCard$ = combineLatest([this.relatedCards$, this.paymentData.cardNumber$]).pipe(
            map(([cards, savedCard]) =>
                savedCard
                    ? cards.find((card) => card.id === savedCard.id || card.card_nr === savedCard.card_nr) || savedCard
                    : null
            )
        );

        this.selectedCardId$ = this.selectedCard$.pipe(map((card) => (card ? card.id : null)));

        this.paymentData.ticketCreationError$.pipe(takeUntil(this.componentDestroyed$)).subscribe();

        this.cardForm.statusChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            this.cardFormValidity.emit(this.cardForm.valid);
        });
    }

    onCardSelectionChange(event: MatSelectChange): void {
        this.paymentData.setCard(event.value);
    }

    handleCardNumberAdded(cardNumber: number): void {
        const newCard: RelatedCard = { id: null, name: cardNumber.toString(), card_nr: cardNumber.toString() };

        this.paymentData.setCard(newCard);
        this.relatedCardFacade.fetchAllRelatedCards();
    }

    addNewCard(): void {
        this.enterNewCardModal.openModal();
        this.matSelect.close();
    }

    topUpOtherCard(): void {
        this.topUpOtherPersonCardModal.openModal();
        this.matSelect.close();
    }

    onCardNumberInput(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const cardNumber = inputElement.value;

        if (this.cardForm.valid) {
            const cardData: RelatedCard = { id: null, name: cardNumber, card_nr: cardNumber };
            this.paymentData.setCard(cardData);
        } else {
            this.paymentData.setCard(null);
        }
    }
}
